import React, {useEffect} from 'react';
import {Navigate, NavLink, Outlet} from 'react-router-dom';
import {useAppDispatch, useAppSelector} from '../../store/hooks';
import {
  adminAccessDeniedSelector,
  adminAlertsSelector,
  adminStatsApiSelector,
} from '../../store/selectors/admin.selectors';
import {fetchAdminStats} from '../../store/slices/adminThunks';
import {AlertTriangleIcon} from '../Icons';

/**
 * The gate and the chrome for `/admin/*`.
 *
 * **The server is the only authority.** A non-admin gets 404 from every admin route — deliberately,
 * so the API does not confirm it exists — and the first one to come back sets `accessDenied`, which
 * bounces the whole section. The `censor_me_is_admin` JWT claim never appears here: it decides
 * whether the header draws a link, and it is stale the moment the flag is revoked.
 *
 * Mounting fetches the stats, which does double duty: it is the probe that provokes that 404 on
 * every admin page rather than only on the dashboard, and it keeps the failed-signup count in the
 * nav live wherever the admin happens to be standing.
 *
 * This sits *inside* `ProtectedRoute` in the route table, so there is already a session by the time
 * it renders.
 */
const AdminRoute: React.FC = () => {
  const dispatch = useAppDispatch();
  const accessDenied = useAppSelector(adminAccessDeniedSelector);
  const statsApi = useAppSelector(adminStatsApiSelector);
  const alerts = useAppSelector(adminAlertsSelector);

  useEffect(() => {
    if (statsApi.status === 'idle') {
      dispatch(fetchAdminStats());
    }
  }, [dispatch, statsApi.status]);

  if (accessDenied) {
    // Not an error screen: as far as this account is concerned the section does not exist.
    return <Navigate to="/welcome" replace />;
  }

  return (
    <div className="h-full overflow-y-auto bg-gray-950">
      <div className="mx-auto max-w-7xl space-y-5 px-4 py-6">
        <header className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <h1 className="text-xl font-semibold text-gray-50">Admin</h1>
            <p className="mt-0.5 text-sm text-gray-500">
              Read-only, apart from resending a setup email. Billing changes happen in Paddle.
            </p>
          </div>

          <div className="flex flex-wrap items-center gap-2">
            {alerts.signupsFailed > 0 && (
              <NavLink
                to="/admin/signups?status=failed"
                className="inline-flex items-center gap-1.5 rounded-lg border border-red-500/40
                 bg-red-500/10 px-3 py-1.5 text-xs font-medium text-red-200 transition-colors
                 hover:bg-red-500/20"
              >
                <AlertTriangleIcon className="h-3.5 w-3.5" />
                {alerts.signupsFailed} paid {alerts.signupsFailed === 1 ? 'signup' : 'signups'} locked out
              </NavLink>
            )}

            {/*
              A suppressed address is not a past failure, it is every future send to that person
              being dropped — including the setup email that is their only way in. Worth carrying
              across every admin page for the same reason the failed signups are.
            */}
            {alerts.suppressionsActive > 0 && (
              <NavLink
                to="/admin/emails?tab=suppressions"
                className="inline-flex items-center gap-1.5 rounded-lg border border-amber-500/40
                 bg-amber-500/10 px-3 py-1.5 text-xs font-medium text-amber-200 transition-colors
                 hover:bg-amber-500/20"
              >
                <AlertTriangleIcon className="h-3.5 w-3.5" />
                {alerts.suppressionsActive}{' '}
                {alerts.suppressionsActive === 1 ? 'address' : 'addresses'} suppressed
              </NavLink>
            )}
          </div>
        </header>

        <nav className="flex flex-wrap items-center gap-1 border-b border-gray-800 pb-2">
          <AdminNavLink to="/admin" label="Overview" end />
          <AdminNavLink to="/admin/users" label="Users" />
          <AdminNavLink to="/admin/signups" label="Signups" badge={alerts.signupsFailed} />
          <AdminNavLink to="/admin/emails" label="Email" badge={alerts.suppressionsActive} />
          <AdminNavLink to="/admin/transcriptions" label="Transcriptions" />
          <AdminNavLink to="/admin/wordlists" label="Word lists" />
        </nav>

        <Outlet />
      </div>
    </div>
  );
};

const AdminNavLink: React.FC<{to: string; label: string; end?: boolean; badge?: number}> = ({
  to,
  label,
  end,
  badge,
}) => (
  <NavLink
    to={to}
    end={end}
    className={({isActive}) => `inline-flex items-center gap-1.5 whitespace-nowrap rounded-lg px-2.5
     py-1.5 text-xs font-medium transition-colors ${
  isActive ? 'bg-gray-800 text-white' : 'text-gray-400 hover:bg-gray-800/60 hover:text-gray-100'
  }`}
  >
    {label}
    {badge !== undefined && badge > 0 && (
      <span className="rounded-full bg-red-500/20 px-1.5 text-[10px] tabular-nums text-red-300">
        {badge}
      </span>
    )}
  </NavLink>
);

export default AdminRoute;
